
import { ref } from "vue";
import { useRouter } from "vue-router";
import { toast } from "vue3-toastify";
import { useConversationsStore } from "@/stores/conversations";
import { useClubsStore } from "@/stores/clubs";
import { api } from "@/api/client";

interface NotificationData {
    type?: "message" | "club_message" | "like" | "match" | "system";
    conversation_id?: number | string;
    club_id?: number | string;
    sender_name?: string;
    club_name?: string;
    url?: string;
}


const isSubscribed = ref(false);
const isPermissionGranted = ref(false);
let listenersAttached = false;


function getOneSignal() {
    return (window as any).OneSignal ?? null;
}


export function usePushNotifications() {
    const router = useRouter();
    const conversationsStore = useConversationsStore();
    const clubsStore = useClubsStore();

    function navigateToNotification(data: NotificationData) {
        if (!data) return;

        if (data.type === "club_message" && data.club_id) {
            router.push({ name: "ClubChat", params: { id: String(data.club_id) } });
            return;
        }

        if ((data.type === "message" || data.conversation_id) && data.conversation_id) {
            router.push({ name: "ChatDetail", params: { id: String(data.conversation_id) } });
            return;
        }

        if (data.url) {
            if (data.url.startsWith("/")) {
                router.push(data.url);
            } else {
                window.location.href = data.url;
            }
        }
    }

    function isAlreadyViewing(data: NotificationData): boolean {
        const route = router.currentRoute.value;

        if (data.type === "message" && data.conversation_id) {
            return route.name === "ChatDetail" && route.params.id == String(data.conversation_id);
        }

        if (data.type === "club_message" && data.club_id) {
            return route.name === "ClubChat" && route.params.id == String(data.club_id);
        }

        return false;
    }

    async function showNotification(title: string, body: string, data: NotificationData = {}) {
        if (Notification.permission !== "granted") return;

        const tag = data.conversation_id
            ? `chat-${data.conversation_id}`
            : data.club_id
                ? `club-${data.club_id}`
                : "notification";

        try {
            const reg = await navigator.serviceWorker.ready;
            reg.showNotification(title, {
                body,
                icon: "/icons/icon-192x192.png",
                badge: "/icons/icon-192x192.png",
                tag,
                renotify: true,
                data,
            } as NotificationOptions);
        } catch {
            new Notification(title, {
                body,
                icon: "/icons/icon-192x192.png",
                tag,
                data,
            });
        }
    }

    async function registerToken(): Promise<void> {
        const OS = getOneSignal();
        if (!OS) return;

        const token: string | null | undefined = OS.User?.PushSubscription?.token;
        if (!token) return;

        try {
            await api.post("/push-token", { token, platform: "web" });
            console.log("[Push] Token registered with backend");
        } catch (e) {
            console.warn("[Push] Failed to register token:", e);
        }
    }

    function handleForeground(event: any) {
        const notification = event.notification ?? event.detail?.notification;
        if (!notification) return;

        const data: NotificationData = notification.additionalData ?? {};
        const title = notification.title ?? "TinderOne";
        const body  = notification.body  ?? "";

        event.preventDefault?.();

        if (isAlreadyViewing(data)) {
            _refreshStores(data);
            return;
        }

        toast(
            `<strong>${title}</strong><br/>${body}`,
            {
                dangerouslyHTMLString: true,
                autoClose: 5000,
                position: "top-right",
                onClick: () => navigateToNotification(data),
            }
        );

        _refreshStores(data);
    }

    function handleClick(event: any) {
        const notification = event.notification ?? event.detail?.notification;
        if (!notification) return;

        const data: NotificationData = notification.additionalData ?? {};
        navigateToNotification(data);
    }


    function handleSubscriptionChange(event: any) {
        const optedIn = event.detail?.current?.optedIn ?? event.optedIn ?? false;
        isSubscribed.value = optedIn;
        console.log("[Push] Subscription changed:", optedIn);

        if (optedIn) {
            registerToken().catch(() => {});
        }
    }


    function _refreshStores(data: NotificationData) {
        if (data.type === "message" || data.conversation_id) {
            conversationsStore.fetchConversations(true).catch(() => {});
        }
        if (data.type === "club_message" || data.club_id) {
            clubsStore.fetchClubs(true).catch(() => {});
        }
    }


    function attachListeners() {
        if (listenersAttached) return;
        const OS = getOneSignal();
        if (!OS) {
            console.warn("[Push] OneSignal not ready, listeners not attached");
            return;
        }

        OS.Notifications.addEventListener("foregroundWillDisplay", handleForeground);
        OS.Notifications.addEventListener("click", handleClick);
        OS.User.PushSubscription.addEventListener("change", handleSubscriptionChange);

        listenersAttached = true;
        console.log("[Push] OneSignal listeners attached");

        if (OS.User?.PushSubscription?.optedIn) {
            registerToken().catch(() => {});
        }
    }

    function detachListeners() {
        const OS = getOneSignal();
        if (!OS || !listenersAttached) return;

        OS.Notifications.removeEventListener("foregroundWillDisplay", handleForeground);
        OS.Notifications.removeEventListener("click", handleClick);
        OS.User.PushSubscription.removeEventListener("change", handleSubscriptionChange);

        listenersAttached = false;
    }

    async function requestPermission(): Promise<boolean> {
        const OS = getOneSignal();
        if (!OS) return false;

        try {
            await OS.Notifications.requestPermission();
            const granted = OS.Notifications.permissionNative === "granted";
            isPermissionGranted.value = granted;
            return granted;
        } catch (e) {
            console.warn("[Push] requestPermission failed:", e);
            return false;
        }
    }


    function syncStatus() {
        const OS = getOneSignal();
        if (!OS) return;

        isSubscribed.value = OS.User?.PushSubscription?.optedIn ?? false;
        isPermissionGranted.value = OS.Notifications?.permissionNative === "granted";
    }


    return {
        isSubscribed,
        isPermissionGranted,
        attachListeners,
        detachListeners,
        requestPermission,
        syncStatus,
        registerToken,
        showNotification,
        navigateToNotification,
    };
}